import type { PJResultsType } from '../calculadora-pj';

// 🔥 CONSTANTES OFICIAIS 2025 - HOTFIX APLICADO
const PJ_CONSTANTS = {
  MEI: {
    LIMITE_MENSAL: 6750.00,
    LIMITE_ANUAL: 81000.00,
    DAS: {
      COMERCIO: 76.90,        
      SERVICOS: 80.90,       
      COMERCIO_SERVICOS: 81.90, 
    },
  },
  SIMPLES: {
    LIMITE_ANUAL: 4800000.00,
  },
  PRESUMIDO: {
    // Bases de cálculo presumidas
    SERVICOS_BASE_IRPJ_CSLL: 32, // 32% para serviços
    OUTRAS_BASE_IRPJ_CSLL: 8,    // 8% para outras atividades
    
    // Alíquotas
    IRPJ: 15,           // 15% sobre base presumida
    CSLL: 9,            // 9% sobre base presumida
    PIS: 0.65,          // 0,65% sobre receita bruta
    COFINS: 3.0,        // 3% sobre receita bruta
    ISS_MEDIO: 3.0,     // 3% médio (varia por município)
  },
  SALARIO_MINIMO: 1518.00,    
  INSS: {
    TETO: 8157.41,
    ALIQUOTA_PROLABORE: 11,
  },
  IRRF: {
    VALOR_DEPENDENTE: 189.59,
  },
} as const;

// 🔥 TABELA IRRF 2025 - CORRETA (Lei nº 14.848/2024)
const TABELA_IRRF_2025 = [
  { limite: 2259.20, aliquota: 0, parcelaDeducao: 0 }, 
  { limite: 2826.65, aliquota: 0.075, parcelaDeducao: 169.44 },
  { limite: 3751.05, aliquota: 0.15, parcelaDeducao: 381.44 },
  { limite: 4664.68, aliquota: 0.225, parcelaDeducao: 662.77 },
  { limite: Number.POSITIVE_INFINITY, aliquota: 0.275, parcelaDeducao: 896.00 }
] as const;

// Ramos de atividade
const RAMOS_ATIVIDADE = {
  comercio: { nome: 'Comércio', anexo: 'I', aliquotaInicial: 4.0 },
  industria: { nome: 'Indústria', anexo: 'II', aliquotaInicial: 4.5 },
  servicosGerais: { nome: 'Serviços Gerais', anexo: 'III', aliquotaInicial: 6.0 },
  servicosLimpeza: { nome: 'Serviços de Limpeza/Vigilância', anexo: 'IV', aliquotaInicial: 4.5 },
  servicosIntelectuais: { nome: 'Serviços Intelectuais', anexo: 'V', aliquotaInicial: 15.5 }
} as const;

// Atividades do Lucro Presumido
const ATIVIDADES_PRESUMIDO = {
  servicos: { nome: 'Prestação de Serviços', baseIrpjCsll: PJ_CONSTANTS.PRESUMIDO.SERVICOS_BASE_IRPJ_CSLL },
  outras: { nome: 'Outras Atividades', baseIrpjCsll: PJ_CONSTANTS.PRESUMIDO.OUTRAS_BASE_IRPJ_CSLL }
} as const;

// Tabelas do Simples Nacional
const TABELAS_SIMPLES = {
  I: [
    { min: 0, max: 180000, aliquota: 4.0 },
    { min: 180000.01, max: 360000, aliquota: 7.3 }
  ],
  II: [
    { min: 0, max: 180000, aliquota: 4.5 },
    { min: 180000.01, max: 360000, aliquota: 7.8 }
  ],
  III: [
    { min: 0, max: 180000, aliquota: 6.0 },
    { min: 180000.01, max: 360000, aliquota: 11.2 }
  ],
  IV: [
    { min: 0, max: 180000, aliquota: 4.5 },
    { min: 180000.01, max: 360000, aliquota: 9.0 }
  ],
  V: [
    { min: 0, max: 180000, aliquota: 15.5 },
    { min: 180000.01, max: 360000, aliquota: 18.0 }
  ]
} as const;

// Funções utilitárias
const parseNumericValue = (value: string): number => {
  return Number.parseFloat(value.replace(/[^0-9,-]+/g, "").replace(",", ".")) || 0;
};

const parseIntegerValue = (value: string): number => {
  return Math.max(0, Number.parseInt(value) || 0);
};

const arredondarMoeda = (valor: number): number => Math.round(valor * 100) / 100;

// Validação dos parâmetros
export const validarParametrosCalculo = (
  regime: string,
  remuneracaoBruta: number,
  ramoAtividade: string
): void => {
  if (!regime) {
    throw new Error("Selecione o regime tributário.");
  }
  
  if (remuneracaoBruta <= 0) {
    throw new Error("Remuneração bruta deve ser maior que zero.");
  }
  
  if (regime === 'mei' && remuneracaoBruta > PJ_CONSTANTS.MEI.LIMITE_MENSAL) {
    throw new Error("Remuneração excede o limite do MEI.");
  }
  
  if ((regime === 'simples' || regime === 'presumido') && !ramoAtividade) {
    throw new Error("Selecione o ramo/tipo de atividade.");
  }
};

/**
 * Calcula IRRF sobre pró-labore
 */
export const calcularIRRF = (proLabore: number, dependentes: number) => {
  const deducaoDependentes = dependentes * PJ_CONSTANTS.IRRF.VALOR_DEPENDENTE;
  const baseCalculo = Math.max(0, proLabore - deducaoDependentes);

  if (baseCalculo <= 0) {
    return {
      valor: 0,
      aliquota: 0,
      baseCalculo: arredondarMoeda(baseCalculo),
      deducaoDependentes: arredondarMoeda(deducaoDependentes),
    };
  }

  for (const faixa of TABELA_IRRF_2025) {
    if (baseCalculo <= faixa.limite) {
      const imposto = Math.max(0, (baseCalculo * faixa.aliquota) - faixa.parcelaDeducao);
      return {
        valor: arredondarMoeda(imposto),
        aliquota: arredondarMoeda(faixa.aliquota * 100), // Converter para porcentagem
        baseCalculo: arredondarMoeda(baseCalculo),
        deducaoDependentes: arredondarMoeda(deducaoDependentes),
      };
    }
  }

  // Fallback para última faixa
  const ultimaFaixa = TABELA_IRRF_2025[TABELA_IRRF_2025.length - 1];
  const imposto = Math.max(0, (baseCalculo * ultimaFaixa.aliquota) - ultimaFaixa.parcelaDeducao);
  
  return {
    valor: arredondarMoeda(imposto),
    aliquota: arredondarMoeda(ultimaFaixa.aliquota * 100),
    baseCalculo: arredondarMoeda(baseCalculo),
    deducaoDependentes: arredondarMoeda(deducaoDependentes),
  };
};

const obterAliquotaSimples = (ramoAtividade: string, faturamentoAnual: number): number => {
  const anexo = RAMOS_ATIVIDADE[ramoAtividade as keyof typeof RAMOS_ATIVIDADE]?.anexo;
  const tabela = TABELAS_SIMPLES[anexo as keyof typeof TABELAS_SIMPLES];

  if (!tabela) return RAMOS_ATIVIDADE[ramoAtividade as keyof typeof RAMOS_ATIVIDADE]?.aliquotaInicial || 6.0;

  for (const faixa of tabela) {
    if (faturamentoAnual >= faixa.min && faturamentoAnual <= faixa.max) {
      return faixa.aliquota;
    }
  }

  return tabela[tabela.length - 1].aliquota;
};

const calcularMEI = (
  remuneracaoBruta: number,
  atividadeMEI: string,
  proLabore: number,
  dependentes: number
): PJResultsType => {
  let dasMEI: number;
  switch (atividadeMEI) {
    case 'comercio': dasMEI = PJ_CONSTANTS.MEI.DAS.COMERCIO; break;
    case 'servicos': dasMEI = PJ_CONSTANTS.MEI.DAS.SERVICOS; break;
    case 'comercioServicos': dasMEI = PJ_CONSTANTS.MEI.DAS.COMERCIO_SERVICOS; break;
    default: dasMEI = PJ_CONSTANTS.MEI.DAS.SERVICOS;
  }

  // Para MEI, pró-labore é opcional
  const inssProLabore = proLabore > 0 ? Math.min(proLabore * 0.11, PJ_CONSTANTS.INSS.TETO * 0.11) : 0;
  const irrf = proLabore > 0 ? calcularIRRF(proLabore, dependentes) : { valor: 0, aliquota: 0, baseCalculo: 0, deducaoDependentes: 0 };
  
  const totalDescontos = dasMEI + inssProLabore + irrf.valor;
  const valorLiquido = remuneracaoBruta - totalDescontos;
  const percentualLiquido = (valorLiquido / remuneracaoBruta) * 100;

  const observacoes: string[] = [];
  if (proLabore === 0) {
    observacoes.push('Pró-labore não informado (opcional para MEI)');
  } else if (proLabore === PJ_CONSTANTS.SALARIO_MINIMO) {
    observacoes.push(`Pró-labore considerado como salário mínimo (R$ ${PJ_CONSTANTS.SALARIO_MINIMO.toFixed(2).replace('.', ',')})`);
  }

  return {
    tipo: 'MEI',
    remuneracaoBruta: arredondarMoeda(remuneracaoBruta),
    regime: 'MEI',
    dasMEI: arredondarMoeda(dasMEI),
    proLabore: arredondarMoeda(proLabore),
    proLaboreInformado: proLabore > 0,
    inssProLabore: arredondarMoeda(inssProLabore),
    irrf,
    totalDescontos: arredondarMoeda(totalDescontos),
    valorLiquido: arredondarMoeda(valorLiquido),
    percentualLiquido: arredondarMoeda(percentualLiquido),
    observacoes,
  };
};

/**
 * Calcular Simples Nacional
 */
const calcularSimples = (
  remuneracaoBruta: number,
  ramoAtividade: string,
  aliquotaPersonalizada: number | null,
  proLabore: number,
  dependentes: number
): PJResultsType => {
  let aliquota: number;
  if (aliquotaPersonalizada && aliquotaPersonalizada > 0) {
    aliquota = aliquotaPersonalizada;
  } else {
    const faturamentoAnual = remuneracaoBruta * 12;
    aliquota = obterAliquotaSimples(ramoAtividade, faturamentoAnual);
  }

  const anexo = RAMOS_ATIVIDADE[ramoAtividade as keyof typeof RAMOS_ATIVIDADE]?.anexo || '';
  const impostoSimples = remuneracaoBruta * (aliquota / 100);
  const inssProLabore = Math.min(proLabore * 0.11, PJ_CONSTANTS.INSS.TETO * 0.11);
  const irrf = calcularIRRF(proLabore, dependentes);
  const totalDescontos = impostoSimples + inssProLabore + irrf.valor;
  const valorLiquido = remuneracaoBruta - totalDescontos;
  const percentualLiquido = (valorLiquido / remuneracaoBruta) * 100;

  const observacoes: string[] = [];
  if (proLabore === PJ_CONSTANTS.SALARIO_MINIMO) {
    observacoes.push(`Pró-labore considerado como salário mínimo (R$ ${PJ_CONSTANTS.SALARIO_MINIMO.toFixed(2).replace('.', ',')})`);
  }
  if (aliquotaPersonalizada) {
    observacoes.push('Alíquota informada manualmente');
  }

  return {
    tipo: 'Simples Nacional',
    remuneracaoBruta: arredondarMoeda(remuneracaoBruta),
    regime: 'Simples Nacional',
    anexo,
    aliquota: arredondarMoeda(aliquota),
    impostoSimples: arredondarMoeda(impostoSimples),
    proLabore: arredondarMoeda(proLabore),
    proLaboreInformado: proLabore > PJ_CONSTANTS.SALARIO_MINIMO,
    inssProLabore: arredondarMoeda(inssProLabore),
    irrf,
    totalDescontos: arredondarMoeda(totalDescontos),
    valorLiquido: arredondarMoeda(valorLiquido),
    percentualLiquido: arredondarMoeda(percentualLiquido),
    observacoes,
  };
};

/**
 * Calcular Lucro Presumido
 */
const calcularPresumido = (
  remuneracaoBruta: number,
  tipoAtividade: string,
  proLabore: number,
  dependentes: number
): PJResultsType => {
  const atividade = ATIVIDADES_PRESUMIDO[tipoAtividade as keyof typeof ATIVIDADES_PRESUMIDO];
  const baseIrpjCsll = atividade?.baseIrpjCsll || PJ_CONSTANTS.PRESUMIDO.OUTRAS_BASE_IRPJ_CSLL;

  // Cálculo dos impostos sobre a receita
  const baseCalculoIrpjCsll = remuneracaoBruta * (baseIrpjCsll / 100);
  const irpj = baseCalculoIrpjCsll * (PJ_CONSTANTS.PRESUMIDO.IRPJ / 100);
  const csll = baseCalculoIrpjCsll * (PJ_CONSTANTS.PRESUMIDO.CSLL / 100);
  const pis = remuneracaoBruta * (PJ_CONSTANTS.PRESUMIDO.PIS / 100);
  const cofins = remuneracaoBruta * (PJ_CONSTANTS.PRESUMIDO.COFINS / 100);
  const iss = remuneracaoBruta * (PJ_CONSTANTS.PRESUMIDO.ISS_MEDIO / 100);

  const impostoPresumido = irpj + csll + pis + cofins + iss;
  const aliquotaEfetiva = (impostoPresumido / remuneracaoBruta) * 100;

  // Cálculo do pró-labore (obrigatório)
  const inssProLabore = Math.min(proLabore * 0.11, PJ_CONSTANTS.INSS.TETO * 0.11);
  const irrf = calcularIRRF(proLabore, dependentes);

  const totalDescontos = impostoPresumido + inssProLabore + irrf.valor;
  const valorLiquido = remuneracaoBruta - totalDescontos;
  const percentualLiquido = (valorLiquido / remuneracaoBruta) * 100;

  const observacoes: string[] = [];
  if (proLabore === PJ_CONSTANTS.SALARIO_MINIMO) {
    observacoes.push(`Pró-labore considerado como salário mínimo (R$ ${PJ_CONSTANTS.SALARIO_MINIMO.toFixed(2).replace('.', ',')})`);
  }
  observacoes.push(`Base de cálculo IRPJ/CSLL: ${baseIrpjCsll}% da receita bruta`);

  return {
    tipo: 'Lucro Presumido',
    remuneracaoBruta: arredondarMoeda(remuneracaoBruta),
    regime: 'Lucro Presumido',
    tipoAtividade: atividade?.nome || 'Outras Atividades',
    aliquota: arredondarMoeda(aliquotaEfetiva),
    impostoPresumido: arredondarMoeda(impostoPresumido),
    detalhesImpostos: {
      irpj: arredondarMoeda(irpj),
      csll: arredondarMoeda(csll),
      pis: arredondarMoeda(pis),
      cofins: arredondarMoeda(cofins),
      iss: arredondarMoeda(iss),
    },
    proLabore: arredondarMoeda(proLabore),
    proLaboreInformado: proLabore > PJ_CONSTANTS.SALARIO_MINIMO,
    inssProLabore: arredondarMoeda(inssProLabore),
    irrf,
    totalDescontos: arredondarMoeda(totalDescontos),
    valorLiquido: arredondarMoeda(valorLiquido),
    percentualLiquido: arredondarMoeda(percentualLiquido),
    observacoes,
  };
};

/**
 * Executa o cálculo completo da PJ
 */
export const calcularPJ = (
  regime: string,
  remuneracaoBruta: string,
  ramoAtividade: string,
  atividadeMEI: string,
  aliquotaPersonalizada: boolean,
  aliquotaPersonalizadaValor: string,
  proLabore: string,
  numeroDependentes: string
): PJResultsType => {
  // Parse dos valores
  const remuneracaoNum = parseNumericValue(remuneracaoBruta);
  const proLaboreNum = parseNumericValue(proLabore) || (regime === 'mei' ? 0 : PJ_CONSTANTS.SALARIO_MINIMO);
  const dependentesNum = parseIntegerValue(numeroDependentes);
  const aliquotaPersonalizadaNum = aliquotaPersonalizada ? parseNumericValue(aliquotaPersonalizadaValor) : null;

  // Validação
  validarParametrosCalculo(regime, remuneracaoNum, ramoAtividade);

  // Garantir pró-labore mínimo apenas para Simples Nacional e Lucro Presumido
  const proLaboreAjustado = regime === 'mei' ? proLaboreNum : Math.max(proLaboreNum, PJ_CONSTANTS.SALARIO_MINIMO);

  // Calcular conforme regime
  if (regime === 'mei') {
    return calcularMEI(remuneracaoNum, atividadeMEI, proLaboreAjustado, dependentesNum);
  } else if (regime === 'simples') {
    return calcularSimples(remuneracaoNum, ramoAtividade, aliquotaPersonalizadaNum, proLaboreAjustado, dependentesNum);
  } else if (regime === 'presumido') {
    return calcularPresumido(remuneracaoNum, ramoAtividade, proLaboreAjustado, dependentesNum);
  } else {
    throw new Error('Regime tributário não reconhecido');
  }
};